/**
 * Checkout URL Parameter Parsing Utilities
 * Phase 1: URL Parameter Handling
 *
 * Provides validation and parsing for all checkout URL parameters.
 * Returns null on invalid input to enable graceful error handling.
 */

import { FilterValidation } from '@/utils/validation/filterValidation';
import { parsePaxString } from '@/utils/parsing/paxParser';
import { $t } from '@/composables/translations/useTranslate';
import { HIDE_AUXILIARIES_STEP } from '@/constants/checkout/config';
import type { CheckoutState, Passenger, CheckoutValidationError } from '@/types/checkout';
import type { RoomOccupancy } from '@/types/SearchBar';

/**
 * Parse all checkout parameters from URL
 * Collects validation errors without throwing exceptions
 * Supports multi-room bookings (offerIds, roomCodes, serviceVariantIds)
 */
export function parseCheckoutUrlParams(): CheckoutState {
  const params = new URLSearchParams(window.location.search);
  const errors: CheckoutValidationError[] = [];

  // Parse offerIds (multi-room)
  let offerIds: number[] | null = null;

  if (params.has('offerIds')) {
    offerIds = params
      .get('offerIds')!
      .split('-')
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !isNaN(id) && id > 0);

    if (offerIds.length === 0) {
      errors.push({
        field: 'offerIds',
        message: $t('ibe.checkout.error.missing-product-id', 'Product ID is missing or invalid'),
        translationKey: 'ibe.checkout.error.missing-product-id',
      });
      offerIds = null;
    }
  } else {
    errors.push({
      field: 'offerIds',
      message: $t('ibe.checkout.error.missing-product-id', 'Product ID is missing'),
      translationKey: 'ibe.checkout.error.missing-product-id',
    });
  }

  // Parse product code
  let productCode: string | null = null;
  if (params.has('productCode')) {
    const value = params.get('productCode')!.trim();
    if (value.length > 0) {
      productCode = value;
    } else {
      errors.push({
        field: 'productCode',
        message: $t('ibe.checkout.error.missing-product-code', 'Product code is missing'),
        translationKey: 'ibe.checkout.error.missing-product-code',
      });
    }
  } else {
    errors.push({
      field: 'productCode',
      message: $t('ibe.checkout.error.missing-product-code', 'Product code is missing'),
      translationKey: 'ibe.checkout.error.missing-product-code',
    });
  }

  // Parse roomCodes (multi-room)
  let roomCodes: string[] | null = null;

  if (params.has('roomCodes')) {
    roomCodes = params
      .get('roomCodes')!
      .split('-')
      .map((code) => code.trim())
      .filter((code) => code.length > 0);

    if (roomCodes.length === 0) {
      errors.push({
        field: 'roomCodes',
        message: $t('ibe.checkout.error.missing-room-code', 'Room code is missing'),
        translationKey: 'ibe.checkout.error.missing-room-code',
      });
      roomCodes = null;
    }
  } else {
    errors.push({
      field: 'roomCodes',
      message: $t('ibe.checkout.error.missing-room-code', 'Room code is missing'),
      translationKey: 'ibe.checkout.error.missing-room-code',
    });
  }

  // Parse board code.
  // Distinguish param present (even empty) from param absent:
  //   - present, empty ('') = intentional "no meal" / self-catering → valid
  //   - absent (null)       = required value missing → error
  let boardCode: string | null = null;
  if (params.has('boardCode')) {
    boardCode = params.get('boardCode')!.trim();
  } else {
    errors.push({
      field: 'boardCode',
      message: $t('ibe.checkout.error.missing-board-code', 'Board code is missing'),
      translationKey: 'ibe.checkout.error.missing-board-code',
    });
  }

  // Parse serviceVariantIds (multi-room)
  let serviceVariantIds: number[] | null = null;

  if (params.has('serviceVariantIds')) {
    serviceVariantIds = params
      .get('serviceVariantIds')!
      .split('-')
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !isNaN(id) && id > 0);

    if (serviceVariantIds.length === 0) {
      errors.push({
        field: 'serviceVariantIds',
        message: $t(
          'ibe.checkout.error.missing-service-variant',
          'Service variant is missing or invalid'
        ),
        translationKey: 'ibe.checkout.error.missing-service-variant',
      });
      serviceVariantIds = null;
    }
  } else {
    errors.push({
      field: 'serviceVariantIds',
      message: $t('ibe.checkout.error.missing-service-variant', 'Service variant is missing'),
      translationKey: 'ibe.checkout.error.missing-service-variant',
    });
  }

  // Parse check-in date
  let checkIn: Date | null = null;
  if (params.has('checkIn')) {
    checkIn = FilterValidation.validateDate(params.get('checkIn')!);
    if (!checkIn) {
      errors.push({
        field: 'checkIn',
        message: $t('ibe.checkout.error.missing-check-in', 'Check-in date is missing or invalid'),
        translationKey: 'ibe.checkout.error.missing-check-in',
      });
    }
  } else {
    errors.push({
      field: 'checkIn',
      message: $t('ibe.checkout.error.missing-check-in', 'Check-in date is missing'),
      translationKey: 'ibe.checkout.error.missing-check-in',
    });
  }

  // Parse check-out date
  let checkOut: Date | null = null;
  if (params.has('checkOut')) {
    checkOut = FilterValidation.validateDate(params.get('checkOut')!);
    if (!checkOut) {
      errors.push({
        field: 'checkOut',
        message: $t('ibe.checkout.error.missing-check-out', 'Check-out date is missing or invalid'),
        translationKey: 'ibe.checkout.error.missing-check-out',
      });
    }
  } else {
    errors.push({
      field: 'checkOut',
      message: $t('ibe.checkout.error.missing-check-out', 'Check-out date is missing'),
      translationKey: 'ibe.checkout.error.missing-check-out',
    });
  }

  // Validate date range
  if (checkIn && checkOut && !FilterValidation.validateDateRange(checkIn, checkOut)) {
    errors.push({
      field: 'checkOut',
      message: $t(
        'ibe.checkout.error.invalid-date-range',
        'Check-out date must be after check-in date'
      ),
      translationKey: 'ibe.checkout.error.invalid-date-range',
    });
  }

  // Parse passengers (pax parameter)
  let passengers: Passenger[] | null = null;
  let roomOccupancy: RoomOccupancy | null = null;
  if (params.has('pax')) {
    const paxString = params.get('pax')!;
    roomOccupancy = parsePaxString(paxString);
    passengers = parsePassengers(paxString);
    if (!passengers || passengers.length === 0) {
      errors.push({
        field: 'pax',
        message: $t('ibe.checkout.error.missing-pax', 'Number of travelers is missing or invalid'),
        translationKey: 'ibe.checkout.error.missing-pax',
      });
      passengers = null;
      roomOccupancy = null;
    }
  } else {
    errors.push({
      field: 'pax',
      message: $t('ibe.checkout.error.missing-pax', 'Number of travelers is missing'),
      translationKey: 'ibe.checkout.error.missing-pax',
    });
  }

  // Validate multi-room array lengths match
  if (offerIds && roomCodes && serviceVariantIds) {
    const offerIdsLength = offerIds.length;
    const roomCodesLength = roomCodes.length;
    const serviceVariantIdsLength = serviceVariantIds.length;

    if (offerIdsLength !== roomCodesLength || offerIdsLength !== serviceVariantIdsLength) {
      errors.push({
        field: 'roomCodes',
        message: $t(
          'ibe.checkout.error.array-length-mismatch',
          'Multi-room data is inconsistent: array lengths do not match'
        ),
        translationKey: 'ibe.checkout.error.array-length-mismatch',
      });
      console.error(
        `[CheckoutParsing] Array length mismatch: offerIds=${offerIdsLength}, ` +
          `roomCodes=${roomCodesLength}, serviceVariantIds=${serviceVariantIdsLength}`
      );
    }
  }

  // Log validation errors
  if (errors.length > 0) {
    console.warn(`[CheckoutParsing] Validation errors:`, errors.map((e) => e.field).join(', '));
  }

  // Parse debug mode (optional, no error if missing)
  const isDebugMode = params.get('debug') === 'true';

  return {
    // Core booking data
    offerIds,
    roomCodes,
    serviceVariantIds,

    // Common booking data
    productCode,
    boardCode,
    checkIn,
    checkOut,
    passengers,
    roomOccupancy,

    // Initial state
    roomCombination: null,
    multiRoomAvailabilityResult: null,
    isCheckingAvailability: false,
    availabilityError: null,
    sessionId: null,
    activityId: null,
    nbcProductData: null,
    isLoadingNbc: false,
    nbcError: null,
    activeStep: HIDE_AUXILIARIES_STEP ? 1 : 0,
    mealDescription: null,
    popupHints: null,

    // Debug mode
    isDebugMode,
  };
}

/**
 * Parse pax parameter string to passenger array
 * Format: "r35-35r10-7" → passengers with room assignments
 * Convention: 35 = adult, actual number = child age
 * New: Includes roomNumber (1-based) for each passenger
 */
export function parsePassengers(paxString: string): Passenger[] | null {
  if (!paxString || paxString.trim().length === 0) {
    return null;
  }

  try {
    const roomOccupancy = parsePaxString(paxString);

    if (!roomOccupancy || !roomOccupancy.rooms || roomOccupancy.rooms.length === 0) {
      console.warn(`[CheckoutParsing] Invalid pax format: ${paxString}`);
      return null;
    }

    // Flatten rooms into passenger array with room numbers
    const passengers: Passenger[] = [];

    roomOccupancy.rooms.forEach((room, roomIndex) => {
      room.persons.forEach((person) => {
        const age = person.type === 'adult' ? 35 : person.age!;
        passengers.push({
          age,
          isAdult: person.type === 'adult',
          isChild: person.type === 'child',
          roomNumber: roomIndex + 1, // 1-based room index
          // Hotel classification from the annotated pax (undefined for plain URLs).
          hotelClass: person.hotelClass,
        });
      });
    });

    return passengers.length > 0 ? passengers : null;
  } catch (error) {
    console.error('[CheckoutParsing] Error parsing pax string:', paxString, error);
    return null;
  }
}
